/**
 * Curated demo pairs. Each slug maps to two FLP files served as static
 * assets from the bundle. Visiting `#/diff/{slug}` (or `/diff/{slug}`
 * on hosts with SPA rewrites) auto-loads the pair.
 *
 * Add a new pair by dropping files under `web/public/demos/{slug}/`
 * 
 * and registering the slug here.
 */

import { parseFLPFile } from "flpdiff";
import { setPair, type LoadedFile } from "./state.ts";

export interface DemoPair {
  readonly slug: string;
  readonly label: string;
  readonly beforePath: string;
  readonly afterPath: string;
  readonly beforeName: string;
  readonly afterName: string;
}

export const DEMOS: readonly DemoPair[] = [
  {
    slug: "dorn-girls",
    label: "Ivan Dorn / The Dare mashup (real-world diff: vocal track added, insert renamed, 9-clip bulk add)",
    beforePath: "/demos/dorn-girls/before.flp",
    afterPath: "/demos/dorn-girls/after.flp",
    beforeName: "dorn-girls.flp",
    afterName: "dorn-girls_2.flp",
  },
];

export function findDemo(slug: string): DemoPair | undefined {
  return DEMOS.find((d) => d.slug === slug);
}

/** Read slug from `/diff/{slug}` or `#/diff/{slug}` (hash wins). */
export function routeSlugFromLocation(): string | null {
  const hash = window.location.hash;
  if (hash.startsWith("#/diff/")) {
    return decodeURIComponent(hash.slice("#/diff/".length));
  }
  const path = window.location.pathname;
  if (path.startsWith("/diff/")) {
    return decodeURIComponent(path.slice("/diff/".length));
  }
  return null;
}

export async function loadDemo(pair: DemoPair): Promise<void> {
  const [beforeBuf, afterBuf] = await Promise.all([
    fetchArrayBuffer(pair.beforePath),
    fetchArrayBuffer(pair.afterPath),
  ]);
  const a: LoadedFile = {
    name: pair.beforeName,
    bytes: beforeBuf.byteLength,
    project: parseFLPFile(beforeBuf),
  };
  const b: LoadedFile = {
    name: pair.afterName,
    bytes: afterBuf.byteLength,
    project: parseFLPFile(afterBuf),
  };
  setPair(a, b);
}

async function fetchArrayBuffer(path: string): Promise<ArrayBuffer> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  return res.arrayBuffer();
}
